import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { handleError } from '../utils/utils';

import { CreateOrUpdateTag, Tag } from '../types/tagTypes';

type BasepathOption = {
    basepath?: string;
};

const DEFAULT_BASEPATH = '/api';
export const TAGS_QUERY_KEY = 'tags';

const getBasepath = (basepath?: string) => basepath ?? DEFAULT_BASEPATH;
const getTagsUrl = (basepath: string) => `${basepath}/tags`;
const getTagUrl = (basepath: string, tagId: string) =>
    `${basepath}/tags/${tagId}`;
const getTagsQueryKey = (basepath: string) =>
    [TAGS_QUERY_KEY, basepath] as const;
const getTagQueryKey = (basepath: string, tagId: string) =>
    [TAGS_QUERY_KEY, basepath, tagId] as const;

export function useTags(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const query = useQuery({
        queryKey: getTagsQueryKey(basepath),
        queryFn: async () => {
            const { data } = await axios.get<Tag[]>(getTagsUrl(basepath));
            return data;
        },
    });

    return {
        tags: query.data ?? [],
        isLoading: query.isLoading,
        errorMessage: query.error
            ? handleError('Fetch tags error', query.error, false)
            : undefined,
        fetch: query.refetch,
    };
}

export function useTag(tagId: string, options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const query = useQuery({
        queryKey: getTagQueryKey(basepath, tagId),
        queryFn: async () => {
            const { data } = await axios.get<Tag>(getTagUrl(basepath, tagId));
            return data;
        },
        enabled: Boolean(tagId),
    });

    return {
        tag: query.data ?? null,
        isLoading: query.isLoading,
        errorMessage: query.error
            ? handleError('Fetch tag error', query.error, false)
            : undefined,
        fetch: query.refetch,
    };
}

export function useCreateTag(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (body: CreateOrUpdateTag) => {
            const { data } = await axios.post<number>(
                getTagsUrl(basepath),
                body,
            );
            return data;
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: getTagsQueryKey(basepath) });
        },
    });

    return {
        tagId: mutation.data ?? null,
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Create tag error', mutation.error, true)
            : undefined,
        createTag: mutation.mutateAsync,
    };
}

export function useUpdateTag(tagId: string, options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (body: CreateOrUpdateTag) => {
            const { data } = await axios.put<Tag>(
                getTagUrl(basepath, tagId),
                body,
            );
            return data;
        },
        onSuccess: (updatedTag) => {
            client.setQueryData(getTagQueryKey(basepath, tagId), updatedTag);
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: getTagsQueryKey(basepath) });
        },
    });

    return {
        updatedTag: mutation.data ?? null,
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Update tag error', mutation.error, true)
            : undefined,
        updateTag: mutation.mutateAsync,
    };
}

export function useUpdateTagInEnv(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async ({
            tagId,
            body,
        }: {
            tagId: number;
            body: CreateOrUpdateTag;
        }) => {
            const { data } = await axios.put<Tag>(
                getTagUrl(basepath, String(tagId)),
                body,
            );
            return data;
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: getTagsQueryKey(basepath) });
        },
    });

    return {
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Update tag error', mutation.error, true)
            : undefined,
        updateTag: (tagId: number, body: CreateOrUpdateTag) =>
            mutation.mutateAsync({ tagId, body }),
    };
}

export function useDeleteTag(options: BasepathOption = {}) {
    const basepath = getBasepath(options.basepath);
    const client = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (tagId: Tag['id']) => {
            await axios.delete(getTagUrl(basepath, String(tagId)));
        },
        onSuccess: (_data, tagId) => {
            client.removeQueries({
                queryKey: getTagQueryKey(basepath, String(tagId)),
            });
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: getTagsQueryKey(basepath) });
        },
    });

    return {
        isLoading: mutation.isPending,
        errorMessage: mutation.error
            ? handleError('Delete tag error', mutation.error, true)
            : undefined,
        deleteTag: mutation.mutateAsync,
    };
}
